import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {assert} from 'Asserts';
import * as Colors from 'ui-library/Colors';
import makeUseGlobalClientState from 'ui-library/hooks/global-client-state/makeUseGlobalClientState';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

import {useStepConfigEditorContext} from '../StepConfigEditorContext';
import {ExpandableDetailsConfig} from '../StepConfigEditorTypes';
import {StepConfigField, VariableHeaderOrNull} from './StepConfigFieldsTemplate';

export const EXPAND_TOGGLE_VALUE = '__EXPAND_TOGGLE_VALUE__';
const isExpandedGlobalState = makeUseGlobalClientState<boolean>(false);

export interface ExpandableDetailsSectionProps {
  expandableDetails: ExpandableDetailsConfig;
  headerField: StepConfigField;
  /** Renders the expanded body. Called with the field name(s) to exclude from the body */
  renderBody: (excludeFieldNames: Array<string>) => React.ReactNode;
}

export default function ExpandableDetailsSection({
  expandableDetails,
  headerField,
  renderBody,
}: ExpandableDetailsSectionProps): JSX.Element {
  const {clientStateId} = useStepConfigEditorContext();

  // The rendered headerField and expandableDetails.headerField describe the same field from two props.
  // Keep them in sync so the expand-state key and body exclusion match the header that's actually rendered.
  assert(
    headerField.systemVariableName === expandableDetails.headerField,
    `headerField prop (${headerField.systemVariableName}) must match expandableDetails.headerField (${expandableDetails.headerField})`
  );

  const [isExpanded, setIsExpanded] = isExpandedGlobalState.useGlobalClientState(
    `expandable-${expandableDetails.headerField}-${clientStateId}`
  );

  const isInlineStyle = !headerField.editor.props.uiSchema?.fieldSettings?.renderCyclingSelector;

  return (
    <div className={css(expandableDetailStyles.expandableDetailsContainer)}>
      <VariableHeaderOrNull
        variableName={headerField.variableName}
        infoText={headerField.infoText}
        addIcon={headerField.addIcon}
        isCompact={true}
      />
      <div className={css(expandableDetailStyles.rowWrapper)}>
        <div className={css(expandableDetailStyles.grow, expandableDetailStyles.headerField)}>
          {React.cloneElement(headerField.editor, {isExpanded})}
        </div>
        <IconButton isExpanded={isExpanded} setIsExpanded={setIsExpanded} isInlineStyle={isInlineStyle} />
      </div>

      {isExpanded && (
        <div className={css(expandableDetailStyles.detailFields)}>
          {renderBody([expandableDetails.headerField])}
        </div>
      )}
    </div>
  );
}

const expandableDetailStyles = StyleSheet.create({
  rowWrapper: {
    display: 'flex',
    flexDirection: 'row',
  },
  grow: {
    flexGrow: 1,
  },
  headerField: {
    marginBottom: '-8px',
  },
  expandableDetailsContainer: {
    marginBottom: 16,
  },
  detailFields: {
    padding: '8px 4px',
    border: `1px solid ${Colors.GRAY_LIGHT}`,
    borderTop: `1px solid ${Colors.GRAY_LIGHT}`,
    borderRadius: '0 0 4px 4px',
    backgroundColor: Colors.GRAY_LIGHTER,
  },
});

function IconButton({
  isExpanded,
  setIsExpanded,
  isInlineStyle,
}: {
  isExpanded: boolean;
  setIsExpanded: (isExpanded: boolean) => void;
  isInlineStyle: boolean;
}): JSX.Element {
  return (
    <div
      className={css(
        buttonStyles.base,
        isExpanded && buttonStyles.expandedColors,
        !isExpanded && buttonStyles.notExpandedColors,

        !isInlineStyle && isExpanded && buttonStyles.tabExpandedBorder,
        !isInlineStyle && !isExpanded && buttonStyles.tabNotExpandedBorder,

        isInlineStyle && buttonStyles.inlineVariantBorder,
        isInlineStyle && isExpanded && buttonStyles.inlineExpandedBorder,
        isInlineStyle && !isExpanded && buttonStyles.inlineNotExpandedBorder
      )}
      onClick={() => {
        setIsExpanded(!isExpanded);
      }}
    >
      <BenchlingIcon icon="filter" />
    </div>
  );
}

const buttonStyles = StyleSheet.create({
  base: {
    width: 28,
    padding: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderStyle: 'solid',
    borderWidth: '1px',
  },

  expandedColors: {
    borderColor: Colors.BLUE_LIGHTEST,
    color: Colors.BLUE_DARKER,
    backgroundColor: Colors.BLUE_POWDER,
  },
  notExpandedColors: {
    borderColor: Colors.GRAY,
    color: Colors.GRAY_DARKER,
    backgroundColor: Colors.GRAY_LIGHT,
  },

  tabExpandedBorder: {
    borderBottomWidth: '0',
    borderRadius: '4px 4px 0 0',
  },
  tabNotExpandedBorder: {
    borderRadius: '4px',
  },

  inlineVariantBorder: {
    borderWidth: '1px 1px 1px 0',
  },
  inlineExpandedBorder: {
    borderRadius: '0 4px 0 0',
    borderBottomWidth: '0',
  },
  inlineNotExpandedBorder: {
    borderRadius: '0 4px 4px 0',
  },
});
